import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SafeHtmlUtils } from '../../../utils/safe-html.util';
import { th } from 'date-fns/locale';
import { ModalService } from 'src/app/services/modal/modal.service';
import { CreationPrescription, EPresciption } from 'src/app/services/ordonnance-numerique/connecteur-models';
import { QRCodeEprescriptionContenu, QRCodeEprescriptionContenuPrescripteur } from 'src/app/services/ordonnance-numerique/interfaces/qrcode.interface';
import { ConsultationModel, ErrorEprescCode, OrdonnanceNumeriqueService, RechercheModel, RechercheResultatModel, TraceEprescriptionType } from 'src/app/services/ordonnance-numerique/ordonnance-numerique.service';
import { EprescriptionInfo } from 'src/app/utils/interfaces/new/eprescription-info.interface';
import { Ordonnance } from 'src/app/utils/interfaces/new/ordonnance';
import { PrescriptionActesMedicaux } from 'src/app/utils/interfaces/new/prescription-actes-medicaux';

export interface InfoEprescriptionBase {
  isSentOk: boolean;
  erreur?: SafeHtml | string;
}

export interface InfoEprescription extends InfoEprescriptionBase {
  qrCodeBase64: string;
  consentement: boolean;
  numeroEprescription: string;
  contenuQrCode: any;
  rawPrescription?: any;
  duplicateNumero?: string;
}

export interface InfoRechercheEprescription extends InfoEprescriptionBase {
  resultatRecherche: Array<RechercheResultatModel>;
}

export interface InfoConsultationEprescription extends InfoEprescriptionBase {
  resultatConsultation: EPresciption;
}

export interface InfoPatientEprescription {
  nom: string;
  prenom: string;
  dateNaissance: string;
  taille?: number;
  poids?: number;
}

@Component({
  selector: 'app-envoi-eprescription',
  templateUrl: './envoi-eprescription.fragment.html',
  styleUrls: [
    './envoi-eprescription.fragment.scss',
    '../modal.fragment.scss'
  ]
})
export class EnvoiEprescription implements OnInit {

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  public modalTitle: string = 'Ordonnance Numérique';
  // Set directement lors de l'ouverture du modal
  public actes: PrescriptionActesMedicaux;
  public ordo: Ordonnance;
  public creationDirect: CreationPrescription;

  public recherche: RechercheModel;
  public consultation: ConsultationModel;

  public infoPatient: InfoPatientEprescription;

  public erreur: SafeHtml | string;
  public loading: boolean = false;

  @Output() public closeEmitter = new EventEmitter();

  retryAction: () => Promise<void>;

  // @HostListener('document:mousedown', ['$event'])
  // onGlobalClick(event): void {
  //   if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
  //     this.closeEnvoiEprescription();
  //   }
  // }

  constructor(
    private _eprescription: OrdonnanceNumeriqueService,
    private _viewContainerRef: ViewContainerRef,
    private _modalService: ModalService,
    protected domSanitizer: DomSanitizer
  ) { }

  async ngOnInit(): Promise<void> {
    if(this.actes) {
      this.modalTitle = 'Ordonnance Numérique - Actes Médicaux';

      this.generateEprescriptionActes();
    } else if(this.ordo) {
      this.modalTitle = 'Ordonnance Numérique - Médicaments & DM';

      this.generateEprescriptionMedicamentDM();
    } else if(this.creationDirect) {
      this.modalTitle = 'Ordonnance Numérique - Création Direct / Duplication';

      this.generateEprescriptionDirect();
    } else if(this.recherche) {
      this.modalTitle = 'Ordonnance Numérique - Recherche';

      this.rechercherEprescriptions();
    } else if(this.consultation) {
      this.modalTitle = 'Ordonnance Numérique - Consultation';

      this.consulterEprescriptions();
    } else {
      this.modalTitle = 'Ordonnance Numérique';
      this.erreur = 'Aucune ordonnance à envoyer';
    }
  }


  async generateEprescriptionMedicamentDM(): Promise<void> {
    try {
      this.loading = true;
      const consentement = await this._eprescription.recupererConsentement(this._viewContainerRef);
      const info = await this._eprescription.recupererInfoPrescripteur(this._viewContainerRef)
      let numeroFactRempla = undefined
      if (info.RPPS2 && info.F) {
        numeroFactRempla = info.F
      } else if (info.RPPS2 && info.AM) {
        numeroFactRempla = info.AM
      }
      const retourCreation = await this._eprescription.sendPrescriptionMedicamentDM(consentement, this.ordo, this.infoPatient, numeroFactRempla)
      if (!retourCreation) {
        throw new Error('Aucune ordonnance retournée');
      }
      const numero = retourCreation.numeroPrescription;
      const rawPrescription = retourCreation.rawPrescriptionSend;
      const qrCodeReturn = await this.generateAndRetryQRCode(numero, info);

      this.closeEnvoiEprescription({
        contenuQrCode: qrCodeReturn.contenuQrCode,
        qrCodeBase64: qrCodeReturn.qrCode,
        consentement: consentement,
        isSentOk: true,
        numeroEprescription: numero,
        rawPrescription: rawPrescription
      });
    } catch (e) {
      console.error(e);
      this.erreur = this.handleErrorToSafeHtml(e);
      this.retryAction = this.generateEprescriptionMedicamentDM;
    } finally {
      this.loading = false;
    }
  }

  async rechercherEprescriptions(): Promise<void> {
    try {
      this.loading = true;
      const info = await this._eprescription.recupererInfoPrescripteur(this._viewContainerRef)
      if (info.RPPS2 && info.F) {
        this.recherche.numeroFinessRemplace = info.F
      } else if (info.RPPS2 && info.AM) {
        this.recherche.numeroAMRemplace = info.AM
      }
      const resultat = await this._eprescription.rechercherPrescriptions(this.recherche);

      this.closeRechercheEprescription({
        isSentOk: true,
        resultatRecherche: resultat
      });
    } catch (e) {
      if (e instanceof ErrorEprescCode && e.codeErrorTrace) {
      }
      console.error(e);
      this.erreur = this.handleErrorToSafeHtml(e);
      this.retryAction = this.rechercherEprescriptions;
    } finally {
      this.loading = false;
    }
  }

  async consulterEprescriptions(): Promise<void> {
    try {
      this.loading = true;
      const info = await this._eprescription.recupererInfoPrescripteur(this._viewContainerRef)
      if (info.RPPS2 && info.F) {
        this.consultation.numeroFinessRemplace = info.F
      } else if (info.RPPS2 && info.AM) {
        this.consultation.numeroAMRemplace = info.AM
      }
      const resultat = await this._eprescription.consulterPrescription(this.consultation);

      this.closeConsultationEprescription({
        isSentOk: true,
        resultatConsultation: resultat
      });
    } catch (e) {
      if (e instanceof ErrorEprescCode && e.codeErrorTrace) {
      }
      this.erreur = this.handleErrorToSafeHtml(e);
      this.retryAction = this.consulterEprescriptions;
    } finally {
      this.loading = false;
    }
  }

  private handleErrorToSafeHtml(error: Error): SafeHtml {
    if (error && error.message) {
      // échappement des caractères HTML spéciaux avant insertion dans le DOM
      const escaped = error.message
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      // replacement des \n par des <br>
      const message = escaped.replace(/\n/g, '<br />');
      return SafeHtmlUtils.sanitizeHtmlAndBypass(message, this.domSanitizer);
    } else {
      return '';
    }
  }

  private async generateEprescriptionActes() {
    try {
      this.loading = true;
      const consentement = await this._eprescription.recupererConsentement(this._viewContainerRef);
      const info = await this._eprescription.recupererInfoPrescripteur(this._viewContainerRef);
      let numeroFactRempla = undefined
      if (info.RPPS2 && info.F) {
        numeroFactRempla = info.F
      } else if (info.RPPS2 && info.AM) {
        numeroFactRempla = info.AM
      }
      const retourCreation = await this._eprescription.sendPrescriptionActesMedicaux(consentement, this.actes, this.infoPatient, numeroFactRempla)
      if (!retourCreation) {
        throw new Error('Aucune ordonnance retournée');
      }
      const numero = retourCreation.numeroPrescription;
      const rawPrescription = retourCreation.rawPrescriptionSend;

      const qrCodeReturn = await this.generateAndRetryQRCode(numero, info);

      this.closeEnvoiEprescription({
        contenuQrCode: qrCodeReturn.contenuQrCode,
        qrCodeBase64: qrCodeReturn.qrCode,
        consentement: consentement,
        isSentOk: true,
        numeroEprescription: numero,
        rawPrescription: rawPrescription
      });
    } catch (e: any) {
      console.error(e);
      this.erreur = this.handleErrorToSafeHtml(e);
      this.retryAction = this.generateEprescriptionActes;
    } finally {
      this.loading = false;
    }
  }

  private async generateEprescriptionDirect() {
    try {
      this.loading = true;
      const consentement = await this._eprescription.recupererConsentement(this._viewContainerRef);
      const info = await this._eprescription.recupererInfoPrescripteur(this._viewContainerRef);
      let numeroFactRempla = undefined
      if (info.RPPS2 && info.F) {
        numeroFactRempla = info.F
      } else if (info.RPPS2 && info.AM) {
        numeroFactRempla = info.AM
      }
      const retourCreation = await this._eprescription.sendPrescriptionDirect(consentement, this.creationDirect, this.infoPatient, numeroFactRempla)

      const numero = retourCreation.numeroPrescription;
      const rawPrescription = retourCreation.rawPrescriptionSend;

      const qrCodeReturn = await this.generateAndRetryQRCode(numero, info);

      this.closeEnvoiEprescription({
        contenuQrCode: qrCodeReturn.contenuQrCode,
        qrCodeBase64: qrCodeReturn.qrCode,
        consentement: consentement,
        isSentOk: true,
        numeroEprescription: numero,
        rawPrescription: rawPrescription
      });
    } catch (e: any) {
      console.error(e);
      this.erreur = this.handleErrorToSafeHtml(e);
      this.retryAction = this.generateEprescriptionDirect;
    } finally {
      this.loading = false;
    }
  }

  public async generateAndRetryQRCode(numeroEprescription: string, info: QRCodeEprescriptionContenuPrescripteur): Promise<{contenuQrCode:QRCodeEprescriptionContenu, qrCode:string}> {
    const contenuQrCode = {
      ID: numeroEprescription,
      P: {
        N: this.infoPatient.nom,
        P: this.infoPatient.prenom,
        D: this.infoPatient.dateNaissance ? this.infoPatient.dateNaissance : undefined,
      },
      M: info,
    };
    let qrCode: string | undefined;
    try {
      qrCode = await this._eprescription.genererQRCode(contenuQrCode);
    } catch (e) {
      // Second essai
      console.warn('Impossible de générer le QR Code, second tentative');
      console.warn(e);
      try {
        qrCode = await this._eprescription.genererQRCode(contenuQrCode);
      } catch (e) {
        console.error('Impossible de générer le QR Code au second essai');
        console.error(e);
        throw e;
      }
    }

    return {
      contenuQrCode,
      qrCode
    }

  }

  public async retry(): Promise<void> {
    if (this.retryAction) {
      this.erreur = undefined;
      await this.retryAction();
      this.retryAction = undefined;
    }
  }

  public closeAfterError(): void {
    this.closeEnvoiEprescription({
        isSentOk: false,
        erreur: this.erreur,
    });
  }
  public closeEnvoiEprescription(object?: Partial<InfoEprescription>): void {
    if (object) {
      this.closeEmitter.emit(object);
    } else {
      this.closeEmitter.emit(null);
    }
  }

  public closeRechercheEprescription(object?: Partial<InfoRechercheEprescription>): void {
    if (object) {
      this.closeEmitter.emit(object);
    } else {
      this.closeEmitter.emit(null);
    }
  }

  public closeConsultationEprescription(object?: Partial<InfoConsultationEprescription>): void {
    if (object) {
      this.closeEmitter.emit(object);
    } else {
      this.closeEmitter.emit(null);
    }
  }

  public stopPropagation(event): void {
    event.stopPropagation();
  }

  private _toNumber(value?: any, dflt?: number): number | undefined {
    if (value && typeof value === 'string') {
      return Number((value as string).replace(',', '.'));
    } else if (value && typeof value === 'number') {
      return Number(value);
    } else {
      return dflt;
    }
  }
}
